import React, { useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Skeleton, Typography,
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { getStripeAccountRemovalCheck } from '../../apollo/queries'
import { removeStripeRestaurantAccount } from '../../apollo/mutations'
import { Card } from './ui'
import { color, focusRing, radius, type } from './tokens'

const GET_CHECK = gql`${getStripeAccountRemovalCheck}`
const REMOVE = gql`${removeStripeRestaurantAccount}`

// What the owner can do about each blocker, where there is something to do.
const NEXT_STEP = {
  AVAILABLE_BALANCE: 'Open Payouts and use Pay out.',
  PENDING_BALANCE: 'Check back once it has arrived and been paid out.',
  UNSENT_ORDERS: 'Finish or cancel those orders first.',
}

const dangerButton = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: radius.control,
  color: '#fff',
  bgcolor: color.error,
  '&:hover': { bgcolor: '#C3173A' },
  '&.Mui-disabled': { bgcolor: color.error, color: '#fff', opacity: 0.45 },
  ...focusRing,
}

/**
 * Settings, bottom: delete the connected account so the restaurant can onboard
 * again from the beginning. The server refuses while money is unsettled, and
 * this card lists exactly what is in the way before the button is offered.
 */
export default function RemoveAccountCard({ restaurantId, onRemoved }) {
  const { data, loading, error, refetch } = useQuery(GET_CHECK, {
    variables: { restaurantId },
    fetchPolicy: 'network-only',
  })
  const [remove, { loading: removing }] = useMutation(REMOVE)
  const [open, setOpen] = useState(false)
  const [understood, setUnderstood] = useState(false)
  const [removeError, setRemoveError] = useState(null)

  const check = data?.stripeAccountRemovalCheck
  const blockers = check?.blockers || []

  const confirm = async () => {
    setRemoveError(null)
    try {
      await remove({ variables: { restaurantId } })
      setOpen(false)
      onRemoved()
    } catch (e) {
      setRemoveError(e.message)
      refetch()
    }
  }

  return (
    <Card sx={{ borderColor: '#F5C2CD' }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <Typography component="h2" sx={type.sectionTitle}>Remove Stripe account</Typography>
          <Typography sx={{ ...type.small, mt: 0.75, lineHeight: 1.6, maxWidth: 620 }}>
            Permanently deletes the Stripe account RushmyFood pays you through. Your orders and earnings in RushmyFood stay,
            and you can set up payouts again from the beginning at any time.
          </Typography>
        </Box>
        <Button
          onClick={() => { setUnderstood(false); setRemoveError(null); setOpen(true) }}
          disabled={loading || !check || !check.canRemove}
          sx={{ ...dangerButton, bgcolor: 'transparent', color: color.error, border: `1px solid ${color.error}`, '&:hover': { bgcolor: color.errorSoft }, '&.Mui-disabled': { color: color.error, opacity: 0.45 } }}
        >
          Remove account
        </Button>
      </Box>

      {loading && !data && <Skeleton width="60%" sx={{ mt: 2, bgcolor: '#EEF1F5' }} />}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error.message}</Alert>}

      {blockers.length > 0 && (
        <Box sx={{ mt: 2.5, p: 2, borderRadius: '10px', bgcolor: color.subtle, border: `1px solid ${color.divider}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: color.text, mb: 1 }}>Before you can remove it</Typography>
          <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'grid', gap: 1 }}>
            {blockers.map((b) => (
              <Box component="li" key={b.code} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <ErrorOutlineIcon sx={{ fontSize: 18, color: color.warning, mt: '1px' }} />
                <Typography sx={{ fontSize: 14, color: color.text, lineHeight: 1.5 }}>
                  {b.message}
                  {NEXT_STEP[b.code] && <Box component="span" sx={{ color: color.muted }}> {NEXT_STEP[b.code]}</Box>}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Dialog
        open={open}
        onClose={removing ? undefined : () => setOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: radius.card } }}
      >
        <DialogTitle sx={{ fontSize: 19, fontWeight: 600, color: color.text, pb: 1 }}>Remove your Stripe account?</DialogTitle>
        <DialogContent>
          <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 1, color: color.text, fontSize: 14, lineHeight: 1.55 }}>
            <li>The account is deleted at Stripe. This cannot be undone.</li>
            <li>Download any tax documents first. They are removed with the account.</li>
            <li>Until you set up again, your card order earnings are kept for withdrawal instead of being sent to Stripe.</li>
            <li>You can set up payouts again from the beginning at any time.</li>
          </Box>
          <FormControlLabel
            sx={{ mt: 2, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { fontSize: 14, color: color.text, pt: '9px' } }}
            control={
              <Checkbox
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                sx={{ color: color.border, '&.Mui-checked': { color: color.error } }}
              />
            }
            label="I understand this permanently removes the account"
          />
          {removeError && <Alert severity="error" sx={{ mt: 1.5 }}>{removeError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setOpen(false)}
            disabled={removing}
            sx={{ textTransform: 'none', fontWeight: 600, color: color.muted, borderRadius: radius.control, ...focusRing }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={confirm}
            disabled={!understood || removing}
            startIcon={removing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
            sx={dangerButton}
          >
            {removing ? 'Removing…' : 'Remove account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
